package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.components.widgets.CloseIcon
import uz.mint.frontend.i18n.LegalDocument
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.PageGutter
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss
import com.varabyte.kobweb.compose.ui.graphics.Colors

/** Which legal document the overlay is showing, if any. */
enum class LegalDoc { OFFER, PRIVACY }

/**
 * Full-screen legal reader.
 *
 * The body text is Russian on every locale — see [uz.mint.frontend.i18n.LegalTexts]. Only the
 * title and close button follow the active language, exactly as the design does.
 */
@Composable
fun LegalOverlay(title: String, document: LegalDocument, strings: SiteStrings, onClose: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(90)
            .backgroundColor(palette.paper).color(palette.ink)
            .overflow { y(Overflow.Auto) }
            .toAttrs {
                attr("role", "dialog")
                attr("aria-modal", "true")
            }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .position(Position.Sticky).top(0.px).zIndex(2)
                .backgroundColor(palette.paper)
                .borderBottom(2.px, LineStyle.Solid, palette.rule)
                .gap(20.px).padding(topBottom = 16.px, leftRight = PageGutter),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                title,
                Modifier.fontSize(clamp(18.px, 2.vw, 26.px)).fontWeight(FontWeight.ExtraBold)
                    .letterSpacing((-0.025).em),
            )
            Spacer()
            Button(
                attrs = Modifier
                    .size(46.px)
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .display(DisplayStyle.Grid)
                    .justifyContent("center").alignItems("center")
                    .flexShrink(0)
                    .backgroundColor(Colors.Transparent)
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() }
                    .toAttrs { attr("aria-label", strings.legalClose) }
            ) { CloseIcon(color = palette.ink) }
        }

        Column(
            Modifier
                .fillMaxWidth().maxWidth(840.px).marginInlineAuto()
                .padding(top = clamp(28.px, 3.4.vw, 52.px), bottom = 88.px, leftRight = PageGutter)
                .gap(26.px)
        ) {
            SpanText(
                document.revision,
                EyebrowStyle.toModifier().fontSize(13.px).letterSpacing(0.1.em).color(palette.accentText),
            )
            P(Modifier.fontSize(17.px).lineHeight(1.55).toAttrs()) { SpanText(document.intro) }

            document.sections.forEach { section ->
                Column(
                    Modifier.fillMaxWidth().gap(8.px)
                        .borderTop(2.px, LineStyle.Solid, palette.rule).padding(top = 18.px)
                ) {
                    H3(
                        Modifier.fontSize(17.px).fontWeight(FontWeight.ExtraBold)
                            .letterSpacing((-0.015).em).toAttrs()
                    ) { SpanText(section.heading) }
                    P(Modifier.fontSize(15.px).lineHeight(1.6).color(palette.ink2).toAttrs()) {
                        SpanText(section.body)
                    }
                }
            }

            Button(
                attrs = Modifier
                    .alignSelf("flex-start")
                    .backgroundColor(palette.solidBg).color(palette.solidFg)
                    .fontWeight(FontWeight.Bold).fontSize(16.px)
                    .padding(topBottom = 16.px, leftRight = PageGutter)
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() }
                    .toAttrs()
            ) { SpanText(strings.legalClose) }
        }
    }
}
